import React, { useState, useRef, useEffect } from 'react';

import {
  Input,
  CloseButton,
  BaseButton,
  LanguageList,
  useT,
} from '@repo/common';
import ProficiencyLevelList from './ProficiencyLevelList';
import LanguageSkillsBanner from './LanguageSkillsBanner';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-top.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';

const EMPTY = {
  language: '',
  iso: '',
  proficiencyLevel: '',
  test: '',
  score: '',
  issueDate: '',
  showTest: false,
};

/**
 * Language skills 풀페이지 에디터.
 * - Language / Proficiency level 드롭다운
 * - "Add Test or certification" → Test/Score/Issue date 노출
 */
const MobileLanguageSkillsFullPage = ({ initialData, onClose, onSave }) => {
  const t = useT();
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...(initialData || {}),
    showTest: initialData?.showTest || !!initialData?.test,
  }));
  const [openLanguage, setOpenLanguage] = useState(false);
  const [openProficiency, setOpenProficiency] = useState(false);

  const languageRef = useRef(null);
  const proficiencyRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (!openLanguage && !openProficiency) return;
    const handleClickOutside = (e) => {
      if (
        openLanguage &&
        languageRef.current &&
        !languageRef.current.contains(e.target)
      ) {
        setOpenLanguage(false);
      }
      if (
        openProficiency &&
        proficiencyRef.current &&
        !proficiencyRef.current.contains(e.target)
      ) {
        setOpenProficiency(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openLanguage, openProficiency]);

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className='fixed inset-0 z-[60] flex flex-col bg-grayscale-gray00'>
      {/* Header */}
      <div className='relative shrink-0 h-[54px] px-4 py-3 flex items-center justify-between border-b border-grayscale-gray02'>
        <CloseButton onClick={onClose} />
        <span className='flex-1 truncate px-2 text-center typo-b16Strong text-text-primary'>
          {t('Language skills', '언어 능력')}
        </span>
        <div className='size-6' />
      </div>

      {/* Body */}
      <div className='flex-1 overflow-y-auto px-4 pt-[30px] pb-[160px] flex flex-col gap-4'>
        <LanguageSkillsBanner />

        {/* Language */}
        <div ref={languageRef} className='relative'>
          <div onClick={() => setOpenLanguage((v) => !v)}>
            <Input
              label={t('Language', '언어')}
              placeholder={t(
                'Search and add language',
                '언어를 검색해 추가해 주세요',
              )}
              value={form.language}
              readOnly
              hideCount
              rightIcon={openLanguage ? <ArrowTopIcon /> : <ArrowBottomIcon />}
            />
          </div>
          {openLanguage && (
            <div className='relative w-full mt-1.5 z-10'>
              <LanguageList
                onSelect={(lang) => {
                  setForm((prev) => ({
                    ...prev,
                    language: lang.name,
                    iso: lang.iso,
                  }));
                  setOpenLanguage(false);
                }}
              />
            </div>
          )}
        </div>

        {/* Proficiency level */}
        <div ref={proficiencyRef} className='relative'>
          <div onClick={() => setOpenProficiency((v) => !v)}>
            <Input
              label={t('Proficiency level', '숙련도')}
              placeholder={t('Select your level', '숙련도를 선택해 주세요')}
              value={form.proficiencyLevel}
              readOnly
              hideCount
              rightIcon={
                openProficiency ? <ArrowTopIcon /> : <ArrowBottomIcon />
              }
            />
          </div>
          {openProficiency && (
            <div className='relative w-full mt-1.5 z-10'>
              <ProficiencyLevelList
                onSelect={(pl) => {
                  setField('proficiencyLevel', pl);
                  setOpenProficiency(false);
                }}
              />
            </div>
          )}
        </div>

        {/* Test / certification */}
        {!form.showTest ? (
          <BaseButton
            type='secondary'
            icon={<PlusIcon />}
            width='202px'
            onClick={() => setField('showTest', true)}
          >
            {t('Add Test or certification', '시험 또는 자격증 추가')}
          </BaseButton>
        ) : (
          <>
            <Input
              label={t(
                'Test or certification (optional)',
                '시험 또는 자격증 (선택 사항)',
              )}
              placeholder={t(
                'e.g. TOPIK II, IELTS, TOEIC, JLPT N2',
                '예: TOPIK II, IELTS, TOEIC, JLPT N2',
              )}
              value={form.test}
              onChange={(e) => setField('test', e.target.value)}
              hideCount
            />
            <Input
              label={t(
                'Score or grade (optional)',
                '점수 또는 등급 (선택 사항)',
              )}
              placeholder={t(
                'e.g. Level 6, Band 7.5, 950',
                '예: 6급, Band 7.5, 950',
              )}
              value={form.score}
              onChange={(e) => setField('score', e.target.value)}
              hideCount
            />
            <Input
              label={t('Issue date (optional)', '발급일 (선택 사항)')}
              placeholder={t('e.g. Aug 2023', '예: 2023년 8월')}
              value={form.issueDate}
              onChange={(e) => setField('issueDate', e.target.value)}
              hideCount
            />
          </>
        )}
      </div>

      {/* Footer — Add */}
      <div className='shrink-0 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+12px)] border-t border-grayscale-gray02'>
        <button
          type='button'
          onClick={() => onSave(form)}
          className='w-full h-[52px] rounded-[12px] bg-[#1DC37B] text-[#F9F7F4] text-[16px] font-medium transition-colors active:bg-[#009E4F]'
        >
          {t('Add', '추가')}
        </button>
      </div>
    </div>
  );
};

export default MobileLanguageSkillsFullPage;
